import {EntityType, KeyOfficeVisitReport} from "./Model";
import {EntityStore, startEntityStore} from "./resourceStoreBuilder";
import {create} from "zustand";
import Subscription from "../util/Subscription";
import {officeArrayResource} from "./CurrentOfficeStore";
import {VisitReports_Read} from "../util/access/Permission";
import {Lifetime} from "../util/Lifetime";

interface VisitReportStore extends EntityStore<KeyOfficeVisitReport[]>{
    uploadReports: (
        files: File[],
        lifetime: Lifetime,
        onUploadProgress?: (progress: number) => void,
    ) => Promise<KeyOfficeVisitReport[]>,
    deleteReport: (report: KeyOfficeVisitReport) => Promise<void>,
}

export const visitReportSubscription = new Subscription();

export function upsertVisitReports(
    previousReports: KeyOfficeVisitReport[],
    uploadedReports: KeyOfficeVisitReport[],
): KeyOfficeVisitReport[] {
    const uploadedById = new Map<number, KeyOfficeVisitReport>();
    uploadedReports.forEach(report => uploadedById.set(report.id, report));

    return [
        ...uploadedById.values(),
        ...previousReports.filter(report => !uploadedById.has(report.id)),
    ];
}

export const useVisitReportStore = create<VisitReportStore>()(
    officeArrayResource<KeyOfficeVisitReport, VisitReportStore>(EntityType.VisitReports, builder => {
        const rest = builder.crud<{}>('/visits/keys', 'id');

        return {
            remoteValue: builder.all(VisitReports_Read, () => rest.getAll()),
            uploadReports: async (
                files: File[],
                lifetime: Lifetime,
                onUploadProgress?: (progress: number) => void,
            ) => {
                const uploadedReports = await rest.uploadFiles(files, lifetime, onUploadProgress);
                builder.patch(previousReports => upsertVisitReports(previousReports, uploadedReports));
                visitReportSubscription.notify();
                return uploadedReports;
            },
            deleteReport: builder.deleteAndPatch<{}>(rest, visitReportSubscription),
        };
    })
);

startEntityStore(useVisitReportStore);
